import React from "react";
import { JobDescriptionEntity } from "../../Entities/JobDescription";
import { JobPostsPagination } from "../../Interface";
import {
  Card,
  Typography,
  Button,
  Switch,
  Pagination,
  Empty,
  Tooltip,
  Space,
  Skeleton,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import jobDescriptionService from "../../Service/job.description.service";
import { openNotification } from "../../Components/Notification";
import { useState, useEffect } from "react";
import useAccess from "../../Hooks/useAccess";
import SubscriptionWarning from "../../Components/SubscriptionWarning";
import "./JobPostCardView.scss";

interface JobPostCardViewProps {
  jobPosts: JobDescriptionEntity[];
  fetchJobPosts: () => Promise<void>;
  pagination: JobPostsPagination;
  setPagination: (updater: JobPostsPagination | ((prev: JobPostsPagination) => JobPostsPagination)) => void;
}

const JobPostCardView: React.FC<JobPostCardViewProps> = ({
  jobPosts,
  fetchJobPosts,
  pagination,
  setPagination,
}) => {
  const navigate = useNavigate();
  const {
    currentLimits,
    currentSubscription,
    currentPlan,
    changeSubscriptionMeasurements,
  } = useAccess();
  const [open, setOpen] = useState<boolean>(false);
  const [togglingJobId, setTogglingJobId] = useState<string | null>(null);

  useEffect(() => {
    const fetchOnUpdate = () => fetchJobPosts();
    jobDescriptionService.subscribe("JOB_POST_ADDED", fetchOnUpdate);
    return () => {
      jobDescriptionService.unsubscribe("JOB_POST_ADDED", fetchOnUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleJobStatus = async (
    job: JobDescriptionEntity,
    status: boolean,
    e?: React.MouseEvent | React.KeyboardEvent,
  ) => {
    if (e) {
      e.stopPropagation();
    }
    if (currentSubscription?.status === "inactive") {
      openNotification(
        "warning",
        "Upgrade Required",
        "Please upgrade your pack to enjoy your benefits.",
      );
      return;
    }
    let query = status ? "publish" : "unpublish";
    if (
      query === "publish" &&
      currentSubscription &&
      currentPlan &&
      currentLimits?.jobPosts !== undefined &&
      currentLimits?.jobPosts !== -1
    ) {
      if (
        currentSubscription?.measurements.jobPosts >= currentLimits?.jobPosts
      ) {
        return setOpen(true);
      }
    }

    setTogglingJobId(job._id);
    try {
      await jobDescriptionService.updateJobDescriptionStatus(job._id, query);
      if (query === "publish") {
        changeSubscriptionMeasurements(1, "jobPosts");
      } else {
        changeSubscriptionMeasurements(-1, "jobPosts");
      }
      await fetchJobPosts();
      openNotification("success", "Job Post Updated", "Successfully Updated");
    } finally {
      setTogglingJobId(null);
    }
  };

  const navigateToTab = (
    _id: string,
    tabIndex:
      | "details"
      | "applications"
      | "qualified"
      | "interview"
      | "hired"
      | "rejected"
      | "bulk-uploads"
      | "file-uploads",
  ) => {
    const isOnlineInterview = jobPosts.find(
      (job) => job._id === _id,
    )?.onlineInterview;
    let currentTab =
      tabIndex === "interview"
        ? isOnlineInterview
          ? "interview"
          : "shortlisted"
        : tabIndex;
    navigate(`/job-posts/${_id}?tabIndex=${currentTab}`);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      page,
      limit: pageSize,
    }));
  };

  const navigateToJobPost = (
    jobId: string,
    tabIndex: string,
    roundStatus?: string,
  ) => {
    navigate(
      `/job-posts/${jobId}?tabIndex=${tabIndex}${
        roundStatus ? `&roundStatus=${roundStatus}` : ""
      }`,
    );
  };

  const JobPostCard = ({ job }: { job: JobDescriptionEntity }) => {
    const isToggling = togglingJobId === job._id;

    return (
      <Card
        className={`job-post-card ${
          isToggling ? "job-post-card--loading" : ""
        }`}
        hoverable={!isToggling}
        onClick={() => !isToggling && navigateToJobPost(job._id, "details")}
      >
        <Skeleton loading={isToggling} active paragraph={{ rows: 4 }}>
          <Card.Meta
            title={
              <div className="job-post-card-header">
                <Tooltip title={job.title} placement="top">
                  <Typography.Text
                    strong
                    ellipsis={{ expanded: true }}
                    style={{ margin: 0, flex: 1, fontSize: 14 }}
                  >
                    {job.position}
                  </Typography.Text>
                </Tooltip>
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  checked={job.isActive}
                  loading={isToggling}
                  disabled={isToggling}
                  onChange={(checked, e) => {
                    e?.stopPropagation();
                    // Only pass MouseEvent, not KeyboardEvent
                    if (e && "button" in e) {
                      toggleJobStatus(job, checked, e as React.MouseEvent);
                    } else {
                      toggleJobStatus(job, checked);
                    }
                  }}
                  onClick={(checked, e) => {
                    e?.stopPropagation();
                  }}
                  size="default"
                />
              </div>
            }
            description={
              <div className="job-post-card-content">
                <Tooltip title={job.position} placement="top">
                  <Typography.Text
                    type="secondary"
                    ellipsis
                    style={{ fontSize: 12, marginTop: 8 }}
                  >
                    {job.title}
                  </Typography.Text>
                </Tooltip>
                <div className="job-post-card-stats">
                  <div className="stat-item stat-item--vacancies">
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToTab(job._id, "details");
                      }}
                      className="stat-item__button"
                    >
                      <div className="stat-item__content">
                        <Typography.Text strong className="stat-item__value">
                          {job.numberOfOpenings}
                        </Typography.Text>
                        <Typography.Text className="stat-item__label">
                          Vacancies
                        </Typography.Text>
                      </div>
                    </Button>
                  </div>
                  <div className="stat-item stat-item--applied">
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToTab(job._id, "applications");
                      }}
                      className="stat-item__button"
                    >
                      <div className="stat-item__content">
                        <Typography.Text strong className="stat-item__value">
                          {job.numberOfApplications || 0}
                        </Typography.Text>
                        <Typography.Text className="stat-item__label">
                          Applied
                        </Typography.Text>
                      </div>
                    </Button>
                  </div>
                  <div className="stat-item stat-item--qualified">
                    <Button
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToJobPost(job._id, "applications", "qualified");
                      }}
                      className="stat-item__button"
                    >
                      <div className="stat-item__content">
                        <Typography.Text strong className="stat-item__value">
                          {job.numberOfQualifiedApplications || 0}
                        </Typography.Text>
                        <Typography.Text className="stat-item__label">
                          Qualified
                        </Typography.Text>
                      </div>
                    </Button>
                  </div>
                </div>
                <div className="job-post-card-footer">
                  <Typography.Text type="secondary">
                    <small>
                      Posted: {dayjs(job.createdAt).format("DD MMM YYYY")}
                    </small>
                  </Typography.Text>
                  <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToJobPost(job._id, "details");
                    }}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            }
          />
        </Skeleton>
      </Card>
    );
  };

  return (
    <div className="job-post-card-view-container">
      {/* All Job Posts - Single list without active/inactive separation */}
      <div className="job-post-card-section">
        {jobPosts.length > 0 ? (
          <div className="job-post-card-scroll-container">
            <div className="job-post-card-list">
              {jobPosts.map((post) => (
                <JobPostCard key={post._id} job={post} />
              ))}
            </div>
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={4}>
                <Typography.Text type="secondary">
                  No job posts yet
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Create your first job post to start receiving applications
                </Typography.Text>
              </Space>
            }
            style={{ padding: "48px 24px" }}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/add-job-post")}
            >
              Create Job Post
            </Button>
          </Empty>
        )}
      </div>

      {/* TODO: Restore active/inactive separation when separate API calls are implemented
      <div className="job-post-card-section">
        <div className="job-post-card-section-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Active Posts ({activePosts.length})
          </Typography.Title>
        </div>
        {activePosts.length > 0 ? (
          <div className="job-post-card-scroll-container">
            <div className="job-post-card-list">
              {activePosts.map((post) => (
                <JobPostCard key={post._id} job={post} />
              ))}
            </div>
          </div>
        ) : (
          <Empty description="No active job posts" style={{ padding: "24px 0" }} />
        )}
      </div>

      <div className="job-post-card-section">
        <div className="job-post-card-section-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Inactive Posts ({inactivePosts.length})
          </Typography.Title>
        </div>
        {inactivePosts.length > 0 ? (
          <div className="job-post-card-scroll-container">
            <div className="job-post-card-list">
              {inactivePosts.map((post) => (
                <JobPostCard key={post._id} job={post} />
              ))}
            </div>
          </div>
        ) : (
          <Empty description="No inactive job posts" style={{ padding: "24px 0" }} />
        )}
      </div>
      */}

      {/* Pagination */}
      <div className="job-posts-pagination">
        <Pagination
          current={pagination.page}
          pageSize={pagination.limit}
          total={pagination.totalRecords}
          onChange={handlePaginationChange}
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} job posts`
          }
        />
      </div>

      <SubscriptionWarning
        open={open}
        handleClose={() => setOpen(false)}
        title="Limit reached"
        type="job-post-limit"
      />
    </div>
  );
};

export default JobPostCardView;
